import React from "react";
import { fmtNum } from "../lib/api";
import { documentDate } from "../lib/signatures";

/**
 * Cover page for Cetak Massal — rekap 10 RW summary.
 * Uses same A5-doc styling for consistency.
 */
export default function RekapCoverA5({ periode, bulan, tahun, rw, kelurahan = "Suka Asih", rekapRw = [], signature }) {
  const totals = rekapRw.reduce((acc, r) => ({
    jumlah_rt: acc.jumlah_rt + (r.jumlah_rt || 0),
    sudah: acc.sudah + (r.sudah_mengisi || 0),
    belum: acc.belum + (r.belum_mengisi || 0),
    total_penduduk: acc.total_penduduk + (r.total_penduduk || 0),
    laki: acc.laki + (r.laki_laki || 0),
    perempuan: acc.perempuan + (r.perempuan || 0),
    tetap: acc.tetap + (r.penduduk_tetap || 0),
    sementara: acc.sementara + (r.penduduk_sementara || 0),
  }), { jumlah_rt: 0, sudah: 0, belum: 0, total_penduduk: 0, laki: 0, perempuan: 0, tetap: 0, sementara: 0 });

  const sigLeft = {
    title: "Mengetahui,",
    subtitle: `Lurah ${kelurahan}`,
    nama: "................",
    ...(signature?.left || {}),
  };
  const sigRight = {
    title: documentDate(bulan, tahun),
    subtitle: `Ketua RW ${rw || "-"}`,
    nama: "................",
    ...(signature?.right || {}),
    title: documentDate(bulan, tahun),
  };

  return (
    <div className="a5-doc fade-in" data-testid="rekap-cover">
      <div className="a5-header">
        <div className="a5-title">REKAPITULASI KEPENDUDUKAN</div>
        <div className="a5-title-2">KELURAHAN {kelurahan.toUpperCase()}</div>
        <div className="a5-title mt-2">RINGKASAN LAPORAN A.5 PER RW</div>
      </div>

      <div className="a5-meta-grid">
        <div className="a5-meta-item">
          <span className="a5-meta-label">Bulan / Tahun</span>
          <span className="a5-meta-colon">:</span>
          <span className="a5-meta-value">{(periode || "-").toUpperCase()}</span>
        </div>
        <div className="a5-meta-item">
          <span className="a5-meta-label">Kelurahan</span>
          <span className="a5-meta-colon">:</span>
          <span className="a5-meta-value">{kelurahan}</span>
        </div>
        <div className="a5-meta-item">
          <span className="a5-meta-label">Kota</span>
          <span className="a5-meta-colon">:</span>
          <span className="a5-meta-value">Bandung</span>
        </div>
        <div className="a5-meta-item">
          <span className="a5-meta-label">Jumlah RW</span>
          <span className="a5-meta-colon">:</span>
          <span className="a5-meta-value">10 RW / 75 RT</span>
        </div>
      </div>

      <div className="a5-table-scroll">
        <table className="a5-table-v2">
          <thead>
            <tr>
              <th rowSpan={2} className="col-no">NO.</th>
              <th rowSpan={2}>RW</th>
              <th colSpan={3}>PELAPORAN</th>
              <th colSpan={5}>PENDUDUK</th>
            </tr>
            <tr>
              <th>JUMLAH RT</th>
              <th>SUDAH</th>
              <th>BELUM</th>
              <th>TOTAL</th>
              <th>L</th>
              <th>P</th>
              <th>TETAP</th>
              <th>SEMENTARA</th>
            </tr>
          </thead>
          <tbody>
            {rekapRw.map((r, i) => (
              <tr key={r.rw}>
                <td className="num">{i + 1}</td>
                <td className="row-label" style={{ textAlign: "center", fontWeight: 700 }}>RW {r.rw}</td>
                <td className="num">{r.jumlah_rt}</td>
                <td className="num">{r.sudah_mengisi}</td>
                <td className="num">{r.belum_mengisi}</td>
                <td className="num jml">{fmtNum(r.total_penduduk)}</td>
                <td className="num">{fmtNum(r.laki_laki)}</td>
                <td className="num">{fmtNum(r.perempuan)}</td>
                <td className="num">{fmtNum(r.penduduk_tetap)}</td>
                <td className="num">{fmtNum(r.penduduk_sementara)}</td>
              </tr>
            ))}
            <tr className="row-sum">
              <td colSpan={2} className="row-label" style={{ textAlign: "center" }}>JUMLAH KELURAHAN</td>
              <td className="num">{totals.jumlah_rt}</td>
              <td className="num">{totals.sudah}</td>
              <td className="num">{totals.belum}</td>
              <td className="num jml">{fmtNum(totals.total_penduduk)}</td>
              <td className="num">{fmtNum(totals.laki)}</td>
              <td className="num">{fmtNum(totals.perempuan)}</td>
              <td className="num">{fmtNum(totals.tetap)}</td>
              <td className="num">{fmtNum(totals.sementara)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="a5-sign-grid">
        <div className="a5-sign-col">
          <div>{sigLeft.title}</div>
          <div className="a5-sign-role">{sigLeft.subtitle}</div>
          <div className="a5-sign-space">
            {sigLeft.url_ttd && <img src={sigLeft.url_ttd} alt="TTD" className="a5-sign-img" />}
            {sigLeft.url_stempel && <img src={sigLeft.url_stempel} alt="Stempel" className="a5-sign-stamp" />}
          </div>
          <div className="a5-sign-name">{sigLeft.nama || "................"}</div>
          {sigLeft.nip && <div className="a5-sign-nip">NIP. {sigLeft.nip}</div>}
        </div>
        <div className="a5-sign-col">
          <div>{sigRight.title}</div>
          <div className="a5-sign-role">{sigRight.subtitle}</div>
          <div className="a5-sign-space">
            {sigRight.url_ttd && <img src={sigRight.url_ttd} alt="TTD" className="a5-sign-img" />}
            {sigRight.url_stempel && <img src={sigRight.url_stempel} alt="Stempel" className="a5-sign-stamp" />}
          </div>
          <div className="a5-sign-name">{sigRight.nama || "................"}</div>
          {sigRight.nip && <div className="a5-sign-nip">NIP. {sigRight.nip}</div>}
        </div>
      </div>
    </div>
  );
}
